<div class="pageheader">
  <div class="page-title">
    <h1>Test Case Interchanges - {{ currentTestCaseName }}</h1>
    <div class="btn-group">
      <button class="btn btn-link" (click)="refresh()">Refresh <ep-svg-icon icon="repeat"></ep-svg-icon></button>
    </div>
  </div>
</div>

<!--
keyExpr tells the DevExtreme DataGrid which field uniquely identifies each row.
We use 'Id' so the grid can:
- Find a row by key (getRowIndexByKey) and navigate to it (navigateToRow).
- Expand a specific master-detail row (expandRow) after rendering.
This is required by onGridContentReady(), which expands and scrolls to the row
matching the pending interchange Id (expandInterchangeId/pendingExpandKey).
-->
<dx-data-grid
  id="gridContainer"
  biz-grid
  [keyExpr]="'Id'"
  [bizSetStateStoring]="false"
  [selection]='{ mode: "single" }'
  [showCollapseAllButton]="true"
  [bizSetScrolling]="false"
  [dataSource]='currentTestCaseInterchanges'
  (onInitNewRow)="onInitNewRow($event)"
  (onRowInserting)="onRowInserting($event)"
  (onRowUpdating)="onRowUpdating($event)"
  (onRowRemoving)="onRowRemoving($event)"
  (onContentReady)="onGridContentReady()">
  <dxo-master-detail [enabled]="true" template="detail"></dxo-master-detail>
  <dxo-editing mode="popup" [allowUpdating]="allowUpdating" [allowAdding]="allowAdding" [allowDeleting]="allowDeleting">
    <dxo-texts confirmDeleteMessage="Are you sure you want to delete this test case interchange?"></dxo-texts>
    <dxo-form>
      <dxi-item dataField="InterchangeIn.Filename"></dxi-item>
      <dxi-item dataField="ContextProperties.Filename"></dxi-item>
      <dxi-item dataField="InterchangeIn.EncodingId"></dxi-item>
      <dxi-item dataField="Note"></dxi-item>
      <dxi-item dataField="InterchangeIn.ContentTypeId"></dxi-item>
    </dxo-form>
  </dxo-editing>

  <ng-container>
    <dxi-column dataField="Note" caption="Note" dataType="string" [visible]="true"></dxi-column>
    <dxi-column dataField="InterchangeIn.Filename" caption="Interchange In File" dataType="string" editCellTemplate="fileEditTemplate" [visible]="true">
      <dxi-validation-rule type="required"></dxi-validation-rule>
    </dxi-column>

    <dxi-column dataField="InterchangeIn.ContentTypeId" dataType="string" caption="Interchange In Content Type" [visible]="true">
      <dxo-lookup [dataSource]="contentTypes" valueExpr="Id" displayExpr="Name" [allowClearing]="true"></dxo-lookup>
    </dxi-column>

    <dxi-column dataField="InterchangeIn.EncodingId" caption="Interchange In Encoding" dataType="string" editCellTemplate="encodingEditTemplate" [visible]="true">
      <dxo-lookup [dataSource]="encodings" valueExpr="EncodingId" [displayExpr]="encodingDisplayExpr"></dxo-lookup>
    </dxi-column>

    <dxi-column dataField="InitConfiguration.Filename" caption="Init Configuration File" dataType="string" editCellTemplate="fileEditTemplate" [visible]="false"></dxi-column>
    <dxi-column dataField="InitConfiguration.ContentType" caption="Init Configuration Content Type" dataType="string" [visible]="false"></dxi-column>
    <dxi-column dataField="InitConfiguration.EncodingId" caption="Init Configuration Encoding" dataType="string" cellTemplate="encodingViewTemplate" editCellTemplate="encodingEditTemplate" [visible]="false"></dxi-column>
    <dxi-column dataField="ContextProperties.Filename" caption="Context Properties File" dataType="string" editCellTemplate="fileEditTemplate" [visible]="true"></dxi-column>
    <dxi-column dataField="ContextProperties.ContentType" caption="Init Configuration Content Type" dataType="string" [visible]="false"></dxi-column>
    <dxi-column dataField="ContextProperties.EncodingId" caption="Init Configuration Encoding" dataType="string" cellTemplate="encodingViewTemplate" editCellTemplate="encodingEditTemplate" [visible]="false"></dxi-column>
    <dxi-column biz-grid-user dataField="CreatedBy" caption="Created By" [visible]="true">
      <dxo-header-filter [dataSource]="bizGridFiltervalues.getUserFilterData(bizGridFiltervalues.getPropertyValues(currentTestCaseInterchanges, 'CreatedBy'))"></dxo-header-filter>
    </dxi-column>
    <dxi-column biz-grid-date dataField="CreatedDate" caption="Created Date" [visible]="true" format="dateTime"></dxi-column>
    <dxi-column biz-grid-user dataField="UpdatedBy" caption="Modified By" [visible]="true">
      <dxo-header-filter [dataSource]="bizGridFiltervalues.getUserFilterData(bizGridFiltervalues.getPropertyValues(currentTestCaseInterchanges, 'UpdatedBy'))"></dxo-header-filter>
    </dxi-column>
    <dxi-column biz-grid-date dataField="UpdatedDate" caption="Modified Date" [visible]="true" format="dateTime"></dxi-column>

    <dxi-column type="buttons" [fixed]="true" fixedPosition="right">
      <dxi-button template="initConfigTemplate"
        hint="Edit/create init configuration for this test case interchange">
      </dxi-button>
      <dxi-button text="Download files"
        hint="Download all files related to this test case interchange"
        [onClick]="downloadExpectedFiles">
      </dxi-button>
      <dxi-button name="edit"></dxi-button>
      <dxi-button name="delete"></dxi-button>
    </dxi-column>
  </ng-container>

  <div *dxTemplate="let row of 'detail'">
    <app-test-tool-test-case-interchange-expected-output-files [testCaseInterchangeId]="row.data.Id"></app-test-tool-test-case-interchange-expected-output-files>
  </div>

  <ng-container *dxTemplate="let cellInfo of 'fileEditTemplate'">
    <span [hidden]="!getCorrectFilename(cellInfo)">Current file: {{ getCorrectFilename(cellInfo) }}</span>
    <ep-file-uploader (fileOnValueChangedEvent)="fileOnValueChangedCallback($event, cellInfo)"
      (fileOnBeforeSendEvent)="fileOnBeforeSendCallback($event, cellInfo)"
      (fileOnUploadedEvent)="fileOnUploadedCallback($event, cellInfo)">
    </ep-file-uploader>
  </ng-container>

  <ng-container *dxTemplate="let cellInfo of 'encodingEditTemplate'">
    <app-encoding-picker (selectedEncodingIdChange)="setEncodingId($event, cellInfo)" [selectedEncodingId]="getCorrectEncodingId(cellInfo)"></app-encoding-picker>
  </ng-container>

  <ng-container *dxTemplate="let cell of 'initConfigTemplate'">
    @if (cell.data.InitConfiguration) {
      <a (click)="editInitConfig(cell.data)" class="dx-link">Edit init-config</a>
    }
    @if (!cell.data.InitConfiguration) {
      <a (click)="editInitConfig(cell.data)" class="dx-link">Create init-config</a>
    }
  </ng-container>
</dx-data-grid>
